import { useEffect } from 'react'
import { subDays } from 'date-fns'

// Shared vocabulary of the Internal Alerts page — mirrors the backend enums / DTOs in
// com.prozo.proship.alerts.internal (AlertHealth, InternalAlertDefinition, InternalAlertDispatch).
export { count, downloadRowsAsCsv, saveBlob, dayStr } from '../freight-profitability/freightPnlUtils'

/**
 * Channels (definition.channel). WhatsApp alerts are Control Tower's template jobs + Proship's own; EMAIL / SMS
 * definitions are Proship's internal mails / SMS whose recipients used to be *.to/cc/bcc properties — same
 * catalogue, history, health and change log, one tab per channel. A missing channel on an old document = WHATSAPP.
 */
export const CHANNELS = [
  { key: 'WHATSAPP', label: 'WhatsApp', short: 'WA', recipientNoun: 'number', addressLabel: 'Mobile', serviceNote: 'Messages go out through the shared WhatsApp service; this page reads the delivery log it writes. "Accepted" means the service took the message for that number; "Delivered" and "Read" are WhatsApp receipts.' },
  { key: 'EMAIL', label: 'Email', short: 'Email', recipientNoun: 'address', addressLabel: 'Email', serviceNote: 'Mails go out through Amazon SES; each send is recorded here by Proship itself, one row per To / CC / BCC address (SES gives no read receipts). A group mailbox is a normal recipient.' },
  // Last: Proship sends no internal SMS today (every SMS goes to a customer / merchant / user mobile); the tab is ready for the first one.
  { key: 'SMS', label: 'SMS', short: 'SMS', recipientNoun: 'number', addressLabel: 'Mobile', serviceNote: 'SMS go out through the SMS gateway; each send is recorded here by Proship itself (no delivery receipts exist for SMS).' },
]
export const channelOf = (d) => (d?.channel ? String(d.channel).toUpperCase() : 'WHATSAPP')
export const channelMeta = (key) => CHANNELS.find(c => c.key === key) || CHANNELS[0]
export const isEmailChannel = (key) => key === 'EMAIL'

/** The one identifier a recipient / dispatch row is keyed by: the mobile for WhatsApp & SMS, the email for mail. */
export const recipientAddress = (r) => r?.mobile || r?.email || ''
export const HEADER_TYPES = [{ value: 'TO', label: 'To' }, { value: 'CC', label: 'CC' }, { value: 'BCC', label: 'BCC' }]
export const headerLabel = (h) => HEADER_TYPES.find(x => x.value === (h || 'TO'))?.label || h

export const SOURCES = [
  { key: 'CONTROL_TOWER', label: 'Control Tower', short: 'CT' },
  { key: 'PROSHIP', label: 'Proship', short: 'Proship' },
]
export const sourceShort = (key) => SOURCES.find(s => s.key === key)?.short || key || '—'
/** Who FIRES the alert (definition.sender) — the Phase 2 cutover switch. */
export const senderLabel = (key) => (key === 'PROSHIP' ? 'Proship' : key === 'CONTROL_TOWER' ? 'Control Tower' : key || '—')
export const sourceLabel = (key) => SOURCES.find(s => s.key === key)?.label || key || '—'
/**
 * Migration state of an alert. Until the sender flag is flipped to PROSHIP (admin API), Control Tower still sends it
 * with its own hardcoded recipients, so the row is read-only here — the backend rejects edits with 409 as well.
 */
export const isLocked = (d) => Boolean(d) && d.sender !== 'PROSHIP'

/** Health states in severity order (worst last), with the badge tone (keys of sharedStyles.statusBadgeSx). */
export const HEALTH = {
  OK:         { label: 'OK',            tone: 'success', help: 'Ran on time, every message accepted, every active recipient reached.' },
  NOT_TRACKED: { label: 'Not tracked',  tone: 'neutral', help: 'A recipient list other mails only read (a BCC list, the tech mailbox). Sends are not recorded against it, so there is nothing to judge.' },
  PENDING:    { label: 'Pending',       tone: 'info',    help: 'Inside today\'s expected window, nothing sent yet.' },
  DISABLED:   { label: 'Disabled',      tone: 'neutral', help: 'Switched off in the definition.' },
  NEVER_SENT: { label: 'Never sent',    tone: 'neutral', help: 'No message has ever been recorded for this template.' },
  LATE:       { label: 'Late',          tone: 'warning', help: 'Ran, but the first message came after the expected window closed.' },
  PARTIAL:    { label: 'Partial',       tone: 'warning', help: 'Ran, but some messages failed or a configured recipient was not reached.' },
  FAILING:    { label: 'Failing',       tone: 'failed',  help: 'Ran, and every message failed.' },
  MISSED:     { label: 'Missed',        tone: 'failed',  help: 'Expected window closed and no run happened.' },
}
export const healthMeta = (state) => HEALTH[state] || { label: state ? String(state) : 'Not evaluated', tone: 'neutral', help: 'Health has not been evaluated yet — the ingest job fills it within 5 minutes.' }

export const PROBLEM_STATES = ['FAILING', 'MISSED']
export const ATTENTION_STATES = ['PARTIAL', 'LATE']

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']   // ISO: 1 = Monday
export function cadenceLabel(c) {
  if (!c || !c.type) return 'No schedule'
  if (c.type === 'DAILY') return `Daily · ${c.windowStartIst || '?'}–${c.windowEndIst || '?'} IST`
  if (c.type === 'WEEKLY') return `Weekly · ${WEEKDAYS[(c.dayOfWeek || 1) - 1]} · ${c.windowStartIst || '?'}–${c.windowEndIst || '?'} IST`
  if (c.type === 'MONTHLY') return `Monthly · day ${c.dayOfMonth || 1}${c.windowEndIst ? ` by ${c.windowEndIst} IST` : ''}`
  return c.type
}

/** Backend dates arrive as "yyyy-MM-dd HH:mm:ss" in IST. */
export const shortTime = (s) => (s ? String(s).slice(0, 16) : '—')
export const dayOf = (s) => (s ? String(s).slice(0, 10) : '—')

/** "2h 05m ago" from an IST "yyyy-MM-dd HH:mm:ss" string. */
export function relTime(s) {
  if (!s) return ''
  const d = new Date(String(s).replace(' ', 'T') + '+05:30')
  if (Number.isNaN(d.getTime())) return ''
  const minutes = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const h = Math.floor(minutes / 60), m = minutes % 60
  if (h < 24) return `${h}h ${String(m).padStart(2, '0')}m ago`
  const days = Math.floor(h / 24)
  return `${days}d ${h % 24}h ago`
}

/** Delivery outcome of one message → badge tone + label. Email / SMS rows only ever reach "Sent" (no receipts). */
export function deliveryOf(row) {
  if (!row) return { label: '—', tone: 'neutral' }
  if (row.status === 'failed') return { label: 'Failed', tone: 'failed' }
  const ch = channelOf(row)
  switch (row.currentStatus) {
    case 'Read': return { label: 'Read', tone: 'success' }
    case 'Delivered': return { label: 'Delivered', tone: 'success' }
    case 'Sent': return { label: ch === 'EMAIL' ? 'Sent (SES)' : 'Sent', tone: ch === 'WHATSAPP' ? 'info' : 'success' }
    default: return { label: row.status === 'success' ? 'Accepted' : (row.status || '—'), tone: 'pending' }
  }
}

export const recipientLabel = (row) => (row?.displayName ? row.displayName : (recipientAddress(row) || '—'))

/** Quick ranges for the history date picker (IST calendar days). */
export function quickRanges() {
  const today = new Date()
  return [
    { label: 'Today', from: today, to: today },
    { label: 'Yesterday', from: subDays(today, 1), to: subDays(today, 1) },
    { label: 'Last 7 days', from: subDays(today, 6), to: today },
    { label: 'Last 14 days', from: subDays(today, 13), to: today },
    { label: 'Last 30 days', from: subDays(today, 29), to: today },
  ]
}

export const HISTORY_MAX_DAYS = 400
export const LIVE_MAX_DAYS = 31

/** Summarise a lastRun for one-line display: "5 msgs · 4 delivered · 1 failed". */
export function runSummary(r) {
  if (!r) return '—'
  const parts = [`${r.messages ?? 0} msg${r.messages === 1 ? '' : 's'}`]
  if (r.read) parts.push(`${r.read} read`)
  if (r.delivered) parts.push(`${r.delivered} delivered`)
  if (r.sent) parts.push(`${r.sent} sent`)
  if (r.pending) parts.push(`${r.pending} pending`)
  if (r.failed) parts.push(`${r.failed} failed`)
  return parts.join(' · ')
}

/** How often the open page re-reads definitions and health; the backend copies the delivery log every 5 minutes. */
export const LIVE_POLL_MS = 5 * 60 * 1000

/**
 * Refetch when the browser tab becomes visible again. A page left open overnight otherwise keeps showing the morning's
 * state (polling is throttled in background tabs, and the store does not register RTK Query's focus listeners).
 */
export function useRefetchOnVisible(refetch) {
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refetch() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refetch])
}
